import fs from 'node:fs'
import path from 'node:path'
import type { VercelProject, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { MAX_NAME_ATTEMPTS } from '@cli-constants/limits.js'
import { INSPECT_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { runVercel } from '../runner/run-vercel.js'
import { readLinkedProjectId } from './read-linked-project-id.js'
import { resolveMissingScopeChoice } from './resolve-missing-scope-choice.js'
import { versionedVercelProjectName } from './versioned-vercel-project-name.js'
import { withScope } from './with-scope.js'

export async function createAndLinkProject(
  runner: VercelRunner,
  cwd: string,
  base: string,
  options: { env?: NodeJS.ProcessEnv; scope?: string; preferredScope?: string }
): Promise<VercelProject> {
  let projectName = base

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const candidate = versionedVercelProjectName(base, attempt)
    projectName = candidate

    const inspect = await runVercel(
      runner,
      withScope(['project', 'inspect', candidate], options.scope),
      {
        cwd,
        mode: 'capture',
        timeoutMs: INSPECT_TIMEOUT_MS,
        env: options.env
      }
    )
    if (inspect.success) continue

    const add = await runVercel(runner, withScope(['projects', 'add', candidate], options.scope), {
      cwd,
      mode: 'capture',
      env: options.env
    })
    if (add.success) break
    if (/already exists/i.test(`${add.stdout}\n${add.stderr}`)) continue
    break
  }

  // `vercel link` resolves the project name through an existing project.json's org scope, which may
  // differ from the authenticated scope `inspect`/`add` just used, re-attaching to the old project.
  try {
    fs.rmSync(path.join(cwd, '.vercel', 'project.json'), { force: true })
  } catch {}

  let link = await runVercel(
    runner,
    withScope(['link', '--project', projectName, '--yes'], options.scope),
    {
      cwd,
      mode: 'capture',
      env: options.env
    }
  )

  // Non-TTY `link` refuses to apply a default scope on multi-scope accounts and
  // instead prints a structured `missing_scope` payload listing the valid
  // project-owning scopes — retry once with a scope resolved from that list.
  if (!link.success) {
    const fallbackScope = resolveMissingScopeChoice(
      `${link.stdout}\n${link.stderr}`,
      options.scope ?? options.preferredScope
    )
    if (fallbackScope && fallbackScope !== options.scope) {
      link = await runVercel(
        runner,
        withScope(['link', '--project', projectName, '--yes'], fallbackScope),
        {
          cwd,
          mode: 'capture',
          env: options.env
        }
      )
    }
  }

  return {
    name: projectName,
    linked: link.success,
    projectId: readLinkedProjectId(cwd)
  }
}
